import React from 'react'
import { useBreakpoint } from '../../lib/utils/hooks'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  EditIcon,
  EyeIcon,
  Flex,
  H2,
  Input,
  Line,
  P,
  SelectDropdown,
  Span,
} from '../../lib/components'
import {
  convertNumberWithCommas,
  displayDollar,
  formatPercentage,
  formatStatus,
  getCentsField,
} from '../../lib/utils/helpers'
import { Pagination } from '../../lib/components/Pagination'
import { WithdrawalPresenterProps, WithdrawRequest } from '../../lib/types'
import { LG, SM } from '../../lib/utils/constants'
import { withdrawalFilters, withdrawalSort } from '../../lib/utils/message'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const WithdrawalsUserListPresenter = ({
  opportunity,
  withdrawals,
  searchParams,
  currentPage,
  pagination,
  completedWithdrawalsCount,
  totalWithdrawnAmount,
  handleCurrentPage,
  handleSearchParams,
  handleInvestorClick,
  resetSearchParams,
}: WithdrawalPresenterProps) => {
  const { breakpoint } = useBreakpoint()
  const navigate = useNavigate()

  const handleManageWithdrawalRequestClick = (
    opportunitySlug: string,
    opportunityId: number,
    investorId: number,
    id: number,
    opportunityTitle: string,
    email: string,
  ) => {
    logCrmEvent(EngagementEvents.CLICKED_VIEW_WITHDRAWAL, {
      opportunityTitle,
      investorEmail: email,
      withdrawalId: id,
    })

    navigate(
      `/opportunity/${opportunitySlug}/${opportunityId}/client/${investorId}/withdrawal/detail/${id}`,
    )
  }

  const handleInvestorProfileClick = (investorId: number, email: string) => {
    logCrmEvent(EngagementEvents.CLICKED_MANAGE_PROFILE, {
      investorId,
      investorEmail: email,
      page: 'opportunity-withdrawals-list',
    })

    handleInvestorClick(investorId)
  }

  return (
    <Flex variant='column'>
      <H2
        text={`Redemptions for ${opportunity && opportunity.opportunity_title}`}
        classes={['m-48 align-center']}
      />

      <Flex variant={breakpoint && breakpoint > LG ? 'row' : 'column'} classes={['gap-32']}>
        {opportunity && (
          <Card classes={['p-40', 'mb-48']}>
            <P text='Opportunity Details' />
            <Flex variant='space-between'>
              <Span variant='bold' text='Opportunity Name' />
              <Span variant='thin' text={opportunity.opportunity_title} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Asset class' />
              <Span variant='thin' text={opportunity.asset_class} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Minimum Amount' />
              <Span
                variant='thin'
                text={displayDollar(String(opportunity.ticket_size), opportunity.currency_code)}
              />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='IRR' />
              <Span variant='thin' text={formatPercentage(opportunity.investment_return)} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Raise Goal' />
              <Span
                variant='thin'
                text={displayDollar(String(opportunity.raise_goal), opportunity.currency_code)}
              />
            </Flex>
          </Card>
        )}

        {opportunity && (
          <Card classes={['p-40', 'mb-48']}>
            <P text='Withdrawal Metrics' />
            <Flex variant='space-between'>
              <Span variant='bold' text='Total completed withdrawals' />
              <Span variant='thin' text={convertNumberWithCommas(completedWithdrawalsCount)} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Total amount withdrawn' />
              <Span
                variant='thin'
                text={displayDollar(
                  String(totalWithdrawnAmount),
                  opportunity.currency_code,
                  2,
                  true,
                )}
              />
            </Flex>
          </Card>
        )}
      </Flex>

      <Flex variant='column-center'>
        <div className='toolbar-layout gap-12 mb-32'>
          <Input
            name='search'
            value={searchParams?.search ?? ''}
            handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
            placeholder='Search'
          />
          <SelectDropdown
            name='filterBy'
            id='filterId'
            formId=''
            list={withdrawalFilters}
            value={searchParams.filter}
            placeholder={'Filter'}
            handleChange={(val) => handleSearchParams({ name: 'filter', value: val })}
            dataTestId='opportunity-withdrawals-filter-list-dropdown'
          />
          <div className='sort-reset'>
            <SelectDropdown
              name='sortBy'
              id='sortById'
              formId=''
              list={withdrawalSort}
              value={searchParams.sort}
              placeholder={'Sort'}
              handleChange={(val) => handleSearchParams({ name: 'sort', value: val })}
              dataTestId='opportunity-withdrawals-sort-list-dropdown'
            />
            <Button
              handleClick={() => resetSearchParams()}
              classes={['reset-btn']}
              variant='secondary-btn'
              text='Reset'
            />
          </div>
        </div>
        <Line />
        <div className='w-100'>
          {withdrawals && withdrawals.length > 0 ? (
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='opportunity-withdrawals-list-table-container'
            >
              <table className={'styled-table'}>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Client Name</th>
                    <th>Client Email</th>
                    <th>Client ID</th>
                    <th>Redemption Request Status</th>
                    <th>Requested amount</th>
                    <th>Actual amount sent</th>
                    <th>Redemption Request Date</th>
                    <th>Manage Redemption Request</th>
                    <th>Client Profile</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(
                    ({
                      id,
                      order_id,
                      investor_id,
                      investor_name,
                      investor_email,
                      investor_reference,
                      status,
                      amount,
                      sell_date,
                      ...withdrawal
                    }: WithdrawRequest) => {
                      const actualAmountSent = getCentsField(
                        withdrawal,
                        'actualAmountSent',
                        'actual_amount_sent',
                      )
                      return (
                        <tr key={id}>
                          <td>{order_id}</td>
                          <td>{investor_name}</td>
                          <td>{investor_email}</td>
                          <td>{investor_reference}</td>
                          <td>{formatStatus(status)}</td>
                          <td data-testid='opportunity-withdrawals-table-withdrawal-amount-cell'>
                            {displayDollar(amount, opportunity.currency_code, 2, true)}
                          </td>
                          <td data-testid='opportunity-withdrawals-table-actual-amount-sent-cell'>
                            {actualAmountSent !== undefined
                              ? displayDollar(
                                  String(actualAmountSent),
                                  opportunity.currency_code,
                                  2,
                                  true,
                                )
                              : '—'}
                          </td>
                          <td data-testid='opportunity-withdrawals-table-withdrawal-request-date-cell'>
                            {sell_date ? new Date(sell_date).toDateString() : 'N.A.'}
                          </td>
                          <td>
                            <EditIcon
                              classes={['align-center', 'pointer']}
                              height='24px'
                              width='24px'
                              onClick={() =>
                                handleManageWithdrawalRequestClick(
                                  opportunity.opportunity_slug,
                                  opportunity.id,
                                  investor_id,
                                  id,
                                  opportunity.opportunity_title,
                                  investor_email,
                                )
                              }
                              dataTestId='opportunity-withdrawals-table-manage-withdrawal-request-icon'
                            />
                          </td>
                          <td>
                            <EyeIcon
                              classes={['align-center', 'pointer']}
                              height='24px'
                              width='24px'
                              onClick={() => {
                                handleInvestorProfileClick(investor_id, investor_email)
                              }}
                              dataTestId='opportunity-withdrawals-table-view-investor-profile-icon'
                            />
                          </td>
                        </tr>
                      )
                    },
                  )}
                </tbody>
              </table>
            </Flex>
          ) : (
            <Flex classes={['column-center']}>
              <Span variant={'large'} text='No Redemption Requests Found' />
              <Span variant={'thin'} text='Try resetting the filters and try again' />
            </Flex>
          )}
        </div>
        {breakpoint && breakpoint === SM
          ? null
          : withdrawals &&
            withdrawals.length > 0 && (
              <Pagination
                currentPage={currentPage}
                handleCurrentPage={handleCurrentPage}
                pagination={pagination}
              />
            )}
      </Flex>
    </Flex>
  )
}

export { WithdrawalsUserListPresenter }
